///
/// EarthBrowser
///
/// Texture series with a speed-aware GPU residency window. Instead of
/// letting every touched frame accumulate on the GPU (~4MB each for ~33
/// synoptic frames), only a small window around the clock time stays
/// uploaded. The window is sized from the observed clock speed (animation
/// or scrubbing) so upcoming frames are preloaded in the direction of
/// travel before they're needed, and frames left behind are purged. Purged
/// computed frames re-fetch their field blobs from the local sqlite
/// database, so no CPU copies are retained either.
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type TextureFrame, TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type PressureFieldTextureFrame } from "./PressureFieldTextureFrame"

const cWindowUpdateIntervalMs = 250
// Preload enough frames ahead to cover this much wall-clock playback
const cLeadSeconds = 4
const cMinAhead = 2
const cMaxAhead = 8
const cKeepBehind = 1
// Cap load kickoffs per update so a large jump fills the window over a few
// ticks instead of stacking up database fetches and texture uploads at once
const cMaxLoadsPerUpdate = 3
// EMA weight per update; reaches steady state in ~1s of ticks
const cSpeedSmoothing = 0.5

// Generic over the frame type so the jet stream shell (JetStreamComputedFrame)
// shares the residency window with the pressure fields
export class PressureFieldTextureSeries<FrameType extends TextureFrame = PressureFieldTextureFrame> extends TextureSeries<FrameType> {
	private ebContext: EarthBrowserContext
	private lastWallMs = 0
	private lastClockMs = 0
	/** Signed, smoothed clock speed in hours of sim time per wall second */
	private speedHoursPerSec = 0

	constructor(context: EarthBrowserContext) {
		super(context)
		this.ebContext = context
		// The base class preloads forward-only and never purges; the window
		// below handles both directions, so disable the built-in preload
		this.preloadCount = 0
	}

	public override updateState(state: RenderState): void {
		super.updateState(state)

		const wallMs = performance.now()
		if (wallMs - this.lastWallMs < cWindowUpdateIntervalMs) return

		const clockMs = this.ebContext.clock.clockTime.date.getTime()
		if (this.lastWallMs > 0) {
			const wallDtSec = (wallMs - this.lastWallMs) / 1000
			const rawSpeed = (clockMs - this.lastClockMs) / 3600000 / wallDtSec
			this.speedHoursPerSec = this.speedHoursPerSec * (1 - cSpeedSmoothing) + rawSpeed * cSpeedSmoothing
		}
		this.lastWallMs = wallMs
		this.lastClockMs = clockMs

		this.applyWindow()
	}

	private applyWindow(): void {
		const frames = this.frames
		const current = this.currentFrame
		if (!current || frames.length === 0) return
		const index = frames.indexOf(current)
		if (index < 0) return

		const frameHours = frames.length > 1
			? Math.max(0.5, (frames[1].range.start.date.getTime() - frames[0].range.start.date.getTime()) / 3600000)
			: 3

		// Frames needed to cover cLeadSeconds of playback at the current speed
		const coverFrames = Math.ceil(Math.abs(this.speedHoursPerSec) * cLeadSeconds / frameHours)
		const ahead = Math.min(cMaxAhead, Math.max(cMinAhead, coverFrames))
		const forward = this.speedHoursPerSec >= 0

		const lo = index - (forward ? cKeepBehind : ahead)
		const hi = index + (forward ? ahead : cKeepBehind)

		// Purge GPU textures that fell out of the window. nextFrame is kept
		// even when out of window: the renderer may be blending toward it
		for (let i = 0; i < frames.length; i++) {
			if (i >= lo && i <= hi) continue
			const frame = frames[i]
			if (frame !== this.nextFrame && frame.available) {
				frame.purge()
			}
		}

		// Upload missing frames inside the window, nearest first, biased
		// toward the direction of travel
		const dir = forward ? 1 : -1
		let loads = 0
		const maxDist = Math.max(index - lo, hi - index)
		for (let d = 0; d <= maxDist && loads < cMaxLoadsPerUpdate; d++) {
			for (const i of d === 0 ? [index] : [index + dir * d, index - dir * d]) {
				if (i < lo || i > hi || i < 0 || i >= frames.length) continue
				const frame = frames[i]
				if (!frame.available) {
					frame.load()
					if (++loads >= cMaxLoadsPerUpdate) break
				}
			}
		}
	}
}
